import React, { useState } from "react";
import styles from "./learningToolMultipleChoice.module.css";
import LearningToolButtons from "../learningToolButtons/LearningToolButtons";
import LearningUnitButtons from "../learningUnitButtons/LearningUnitButtons";
import LearningToolModal from "../learningToolModal/LearningToolModal";
import MultipleChoiceModal from "./multipleChoiceModal/MultipleChoiceModal";
import axios from "axios";

const LearningToolMultipleChoice = ({
  tool,
  mayEditTool,
  removeToolFromList,
  maxUnitsPerTool,
}) => {
  /* The LearningToolMultipleChoice component is responsible for displaying, adding, editing, removing, and checking answers of a Multiple Choice learning tool. Each tool may contain one or more learning units. A Multiple Choice learning unit is an exercise in which the user should select the correct answer for a given question. */

  const [toolUnits, setToolUnits] = useState(tool.units);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [activeModalUnit, setActiveModalUnit] = useState(null);
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [isUnitContentModalOpen, setIsUnitContentModalOpen] = useState(false);
  const [isNewUnitContentModalOpen, setIsNewUnitContentModalOpen] =
    useState(false);
  const [activeContentModalUnit, setActiveContentModalUnit] = useState(null);
  const [activeUnitIndex, setActiveUnitIndex] = useState(null);
  const [userSelections, setUserSelections] = useState({});
  const [unitsFeedback, setUnitsFeedback] = useState({});

  const learningToolId = tool.learning_tool_id;
  const learningZoneId = tool.learning_zone_id;

  /* Open the unit content modal to add a new unit */
  const onAddUnit = () => {
    setIsNewUnitContentModalOpen(true);
  };

  /**
   * Open the confirmation modal to confirm a unit deletion
   * @param {*} unitId - the id of the unit to be deleted
   */
  const onDeleteUnit = (unitId) => {
    setActiveModalUnit(unitId);
    setIsUnitModalOpen(true);
  };

  /**
   * Open the confirmation modal to confirm the tool deletion.
   */
  const onDeleteTool = () => {
    setIsToolModalOpen(true);
  };

  /* Close the unit content modal */
  const closeUnitModal = () => {
    setIsUnitModalOpen(false);
  };

  /* Close the tool confirmation modal */
  const closeToolModal = () => {
    setIsToolModalOpen(false);
  };

  /* Close the unit content modal */
  const closeUnitContentModal = () => {
    setIsUnitContentModalOpen(false);
    setIsNewUnitContentModalOpen(false);
  };

  /* Delete a unit from the database and remove it from the page display */
  const handleModalConfirmUnitDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_units/deactivate/${learningToolId}/${activeModalUnit}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        if (toolUnits.length <= 1) {
          removeToolFromList(learningToolId);
        } else {
          setToolUnits((prevUnits) =>
            prevUnits.filter(
              (unit) => unit.learning_unit_id !== activeModalUnit
            )
          );
        }
      } else {
        console.error("Error - unit was not deleted.");
      }
    } catch (error) {
      console.error("Error - unit was not deleted.");
    }

    closeUnitModal();
  };

  /**
   * Delete the tool from the database and remove it from the page display
   **/
  const handleModalConfirmToolDelete = async () => {
    try {
      const res = await axios.put(
        `/learning_tools/deactivate/${learningToolId}`,
        {},
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        removeToolFromList(learningToolId);
      } else {
        console.error("Error - tool was not deleted.");
      }
    } catch (error) {
      console.error("Error - tool was not deleted.");
      console.error(error);
    }

    closeToolModal();
  };

  /**
   * Handle a unit solution selection change (choosing another radio button)
   * @param {*} unitId - the id of the unit in which the solution was selected
   * @param {*} solutionText - the solution text
   */
  const handleSelectionChange = (unitId, solutionText) => {
    setUserSelections((prevSelections) => ({
      ...prevSelections,
      [unitId]: solutionText,
    }));
  };

  /**
   * Check a user answer to a unit
   * @param {*} unitId - the id of the unit to check the answer for
   */
  const handleCheckAnswer = (unitId) => {
    const unitToCheck = toolUnits.find(
      (unit) => unit.learning_unit_id === unitId
    );
    if (!unitToCheck) {
      console.error(`Unit with ID ${unitId} not found.`);
      return;
    }

    const userAnswer = userSelections[unitId];

    const correctAnswer = getUnitCorrectAnswer(unitId);

    const feedbackClass = userAnswer === correctAnswer ? "✔" : "✘";
    const feedbackType = userAnswer === correctAnswer ? "correct" : "incorrect";

    setUnitsFeedback({
      ...unitsFeedback,
      [unitId]: { symbol: feedbackClass, type: feedbackType },
    });
  };

  /**
   * Check the user answers for all the tool units, and set the feedback accordingly.
   */
  const handleCheckAllAnswers = () => {
    const updatedFeedback = {};

    toolUnits.forEach((unit) => {
      const unitId = unit.learning_unit_id;
      const userAnswer = userSelections[unitId] || "";
      const correctAnswer = getUnitCorrectAnswer(unitId);

      updatedFeedback[unitId] = {
        symbol: userAnswer === correctAnswer ? "✔" : "✘",
        type: userAnswer === correctAnswer ? "correct" : "incorrect",
      };
    });

    setUnitsFeedback(updatedFeedback);
  };

  /**
   * Get a unit's correct answer
   * @param {*} unitId - the id of the unit
   * @returns string - the correct answer of the unit
   */
  function getUnitCorrectAnswer(unitId) {
    const reqUnit = toolUnits.find((unit) => unit.learning_unit_id === unitId);
    return reqUnit.correct_answer;
  }

  /**
   * Open the unit content modal to allow the user to edit a unit
   * @param {*} unitId - the id of the unit to edit
   * @param {*} idx - the index of the unit in the tool
   */
  const handleEditUnit = (unitId, idx) => {
    setActiveContentModalUnit(unitId);
    setActiveUnitIndex(idx);
    setIsUnitContentModalOpen(true);
  };

  /**
   * Update the display of a unit with its new content
   * @param {*} updatedUnitContent - object - the new content of the unit
   */
  const handleUnitContentConfirm = (updatedUnitContent) => {
    setToolUnits((prevUnits) =>
      prevUnits.map((unit) =>
        unit.learning_unit_id === activeContentModalUnit
          ? { ...unit, ...updatedUnitContent }
          : unit
      )
    );
    closeUnitContentModal();
  };

  /**
   * Add a new unit to the tool display
   * @param {*} newUnit - object with the new unit data
   */
  const addNewUnitToList = (newUnit) => {
    setToolUnits([...toolUnits, newUnit.newUnit]);
    setIsNewUnitContentModalOpen(false);
  };

  const toolData = toolUnits.map((unit, idx) => (
    <div key={idx} className={styles.singleUnit}>
      <div className={styles.questionContainer}>
        <p className={styles.question}>
          {idx + 1}. {unit.question_text}{" "}
        </p>
        <p
          className={`${styles.unitFeedback} ${
            styles[unitsFeedback[unit.learning_unit_id]?.type]
          }`}
        >
          {unitsFeedback[unit.learning_unit_id]?.symbol}
        </p>
        <LearningUnitButtons
          mayEditTool={mayEditTool}
          isVoc={false}
          unitId={unit.learning_unit_id}
          onCheckAnswer={handleCheckAnswer}
          onEditUnit={() => handleEditUnit(unit.learning_unit_id, idx)}
          onDeleteUnit={onDeleteUnit}
        />
      </div>

      <div className={styles.answersContainer}>
        {unit.unitSolutions.map((solution, solutionIdx) => (
          <div key={solutionIdx} className={styles.singleAnswer}>
            <input
              type="radio"
              id={"mcu" + unit.learning_unit_id + "s" + solutionIdx}
              name={"mcu" + unit.learning_unit_id}
              value={solution.solution_text}
              checked={
                userSelections[unit.learning_unit_id] === solution.solution_text
              }
              onChange={() =>
                handleSelectionChange(
                  unit.learning_unit_id,
                  solution.solution_text
                )
              }
            />
            <label htmlFor={"mcu" + unit.learning_unit_id + "s" + solutionIdx}>
              {solution.solution_text}
            </label>
          </div>
        ))}
      </div>
    </div>
  ));

  return (
    <div className={styles.multipleChoiceTool}>
      <div className={styles.toolButtons}>
        {mayEditTool && (
          <LearningToolButtons
            toolId={learningToolId}
            handleAddNewUnit={onAddUnit}
            onDeleteTool={onDeleteTool}
            unitsNum={toolUnits.length}
            maxUnitsPerTool={maxUnitsPerTool}
          />
        )}
      </div>
      <h2>Multiple Choice Questions</h2>
      <p className={styles.creatorInfo}>By {tool.username}</p>
      <p className={styles.instructions}>Select the correct answer:</p>
      <div className={styles.toolData}>{toolData}</div>
      <div className={styles.checkBtnContainer}>
        <button onClick={handleCheckAllAnswers}>Check All Answers</button>
      </div>
      <LearningToolModal
        message="Are you sure you want to delete this learning unit?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isUnitModalOpen}
        onClose={closeUnitModal}
        onConfirm={handleModalConfirmUnitDelete}
      />

      <LearningToolModal
        message="Are you sure you want to delete this learning tool?"
        approveBtnText="YES, DELETE"
        cancelBtnText="No, cancel"
        show={isToolModalOpen}
        onClose={closeToolModal}
        onConfirm={handleModalConfirmToolDelete}
      />

      <MultipleChoiceModal
        header={`Edit Multiple Choice Unit ${activeUnitIndex + 1}`}
        unitContent={toolUnits.find(
          (unit) => unit.learning_unit_id === activeContentModalUnit
        )}
        approveBtnText="Update"
        cancelBtnText="Cancel"
        show={isUnitContentModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={handleUnitContentConfirm}
        learningToolId={learningToolId}
        learningZoneId={learningZoneId}
      />

      <MultipleChoiceModal
        header={`Add Multiple Choice Unit`}
        unitContent={null}
        approveBtnText="Create"
        cancelBtnText="Cancel"
        show={isNewUnitContentModalOpen}
        onClose={closeUnitContentModal}
        onConfirm={addNewUnitToList}
        learningToolId={learningToolId}
        learningZoneId={learningZoneId}
      />
    </div>
  );
};

export default LearningToolMultipleChoice;
